import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Filter from 'src/components/common/Filter';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import PagedResultSet from 'src/models/common/PagedResultSet';
import ContractGenerationListDto from 'src/models/leasing/ContractGenerationListDto';
import { IRootState } from 'src/reducers';
import AgreementsList from '../../LeasingComponents/AgreementsList';
import { ODataOptions, ODataOrderByDirection } from 'src/util/ODataBuilder';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import { useFetch } from 'use-http';
import { ITableConfig } from 'src/components/common/table';
import Checkbox from 'src/components/controls/forms/Checkbox';
import ContractProcessingComponent from './ContractProcessingComponent';

const ProcessingAndMaintenance = () => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const { response, get } = useFetch();
    
    const agreementsPageTableConfig: ITableConfig = {
        rowCount: 15,
        pageNumber: 1,
        idField: 'id',
        columns: [{ name: 'Number', mapping: 'id' }],
    };

    const defaultBatchOptions: { exercise: boolean; recording: boolean; assign: boolean } = {
        exercise: true,
        recording: true,
        assign: true,
    };

    const [generatedContracts, setGeneratedContracts] = useState<PagedResultSet<ContractGenerationListDto>>(null);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [filterOptions, setFilterOptions] = useState<string>(ODataOptions.from(agreementsPageTableConfig).toString());
    const [selectedContracts, setSelectedContracts] = useState<Array<number>>([]);
    const [batchOptions, setBatchOptions] = useState(defaultBatchOptions);

    useEffect(() => {
        setBatchOptions(defaultBatchOptions);
    }, []);

    useEffect(() => {
        if (filterOptions !== '') {
            getGeneratedContracts();
        }
    }, [filterOptions]);
    
    // const initFilterOptions = () => {
    //     let newOptions = ODataOptions.from(agreementsPageTableConfig);
    //     setFilterOptions(newOptions.toString());     
    // }

    const onIncrementPage = () => {
        if (pageNumber * generatedContracts.pageSize < generatedContracts.totalRecords) {
            getGeneratedContracts();
            setPageNumber(pageNumber + 1);
        }
    };

    const getGeneratedContracts = async () => {
        // Get contracts and assign to generatedContracts.
        let result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/paged/${filterOptions}`);

        if (response.ok) {
            setGeneratedContracts(result);
        } else {
            console.log('getGeneratedContracts Error:', result);
            window.alert('Error getting Contract Data');
        }
    };

    const handleFilterChanged = (options: ODataOptions) => {
        setPageNumber(1);
        let initOptions = ODataOptions.from(agreementsPageTableConfig);

        if (options.getOrderByProperty() === undefined) {
            options.addOrderBy('leaseNumber', ODataOrderByDirection.Descending);
        }

        initOptions.merge(options);
        const stringOptions = initOptions.toString();
        setFilterOptions(stringOptions);
    };

    const processingActionsWindow = () => {
        return (
            <Panel fitPanelToScreen={true} customSize={2}>
                <div>
                    <div className="dlc-content-panel-header">Processing and Maintenance Tools</div>
                    <div style={{ width: '33%', display: 'inline-block' }}>
                        <Checkbox label="Exercise Option" value={batchOptions.exercise} />
                    </div>
                    <div style={{ width: '33%', display: 'inline-block' }}>
                        <Checkbox label="Recording" value={batchOptions.recording} />
                    </div>
                    <div style={{ width: '33%', display: 'inline-block' }}>
                        <Checkbox label="Assign/Transfer" value={batchOptions.assign} />
                    </div>
                </div>
                <div style={{ overflow: 'auto', maxHeight: '65vh' }} id="what">
                    {!!generatedContracts && (
                        <ContractProcessingComponent
                            selectedAgreements={generatedContracts?.items?.filter(gc => selectedContracts.indexOf(gc.agreementId) > -1)}
                        />
                    )}
                    {!generatedContracts && <LoadingSpinner />}
                </div>
            </Panel>
        );
    };

    const generatedContractsPanel = () => {
        return (
            <Panel fitPanelToScreen={true} customSize={2}>
                <div>
                    <div className="dlc-content-panel-header">Active Contracts</div>
                    <Filter
                        sortItems={[
                            { group: 1, text: 'Contract Type', icon: 'alpha-up', orderName: 'leaseType', orderDirection: 'ascending' },
                            { group: 1, text: 'Contract Number', icon: 'alpha-up', orderName: 'leaseNumber', orderDirection: 'ascending' },
                            { group: 1, text: 'Owner Name', icon: 'alpha-up', orderName: 'ownerName', orderDirection: 'ascending' },
                            { group: 1, text: 'Leased By', icon: 'numeric-up', orderName: 'leasedBy', orderDirection: 'ascending' },
                        ]}
                        filterItems={[
                            { group: 1, text: 'Contract Type', orderName: 'leaseType', orderDirection: 'descending' },
                            { group: 1, text: 'Contract Number', orderName: 'leaseNumber', orderDirection: 'descending' },
                            { group: 1, text: 'Owner Name', orderName: 'ownerName', orderDirection: 'descending' },
                            { group: 1, text: 'Leased By', orderName: 'leasedBy', orderDirection: 'descending' },
                        ]}
                        onFilterChanged={handleFilterChanged}
                    />
                </div>
                <div style={{ overflow: 'auto', maxHeight: '65vh' }} id="what">
                    {!!generatedContracts && (
                        <AgreementsList
                            generatedAgreements={generatedContracts}
                            pageType="Processing&Maintenance"
                            clientNumber={appContext.clientNumber}
                            projectNumber={appContext.projectNumber}
                            selectedContracts={selectedContracts}
                            incrementPage={onIncrementPage}
                            setSelectedContracts={setSelectedContracts}
                        />
                    )}
                    {!generatedContracts && <LoadingSpinner />}
                </div>
            </Panel>
        );
    };

    return (
        <Page>
            <Page.Section width={7}>
                {generatedContractsPanel()}
            </Page.Section>
            <Page.Section width={5}>
                <Panel fitPanelToScreen={true} customSize={2}>
                    {processingActionsWindow()}
                </Panel>
            </Page.Section>
        </Page>
    );
};

export default ProcessingAndMaintenance;


// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/ProcessingAndMaintenance/ProcessingAndMaintenance.tsx